
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { securityService } from '@/services/SecurityService';

interface SecurityContextType {
  isSecure: boolean;
  isLoading: boolean;
  checkSecurity: () => Promise<void>;
  sessionValid: boolean;
  profileExists: boolean;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export function SecurityProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [isSecure, setIsSecure] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  
  // Prevent multiple validations
  const isValidating = useRef(false);
  const lastUserId = useRef<string | null>(null);

  const checkSecurity = useCallback(async () => {
    // Prevent concurrent validations
    if (isValidating.current) return;
    
    const currentUserId = user?.id || null;
    
    // Skip if user hasn't changed
    if (lastUserId.current === currentUserId && !isLoading) {
      return;
    }

    isValidating.current = true;
    
    try {
      if (!user) {
        // No user - set states immediately
        setSessionValid(false);
        setProfileExists(false);
        setIsSecure(false);
        setIsLoading(false);
        lastUserId.current = null;
        return;
      }

      // User exists - validate once
      const context = await securityService.validateSecurityContext();
      
      setSessionValid(context.sessionValid);
      setProfileExists(context.profileExists);
      setIsSecure(context.isAuthenticated && context.sessionValid && context.profileExists);
      setIsLoading(false);
      lastUserId.current = currentUserId;

    } catch (error) {
      console.error("Security validation failed:", error);
      setIsSecure(false);
      setSessionValid(false);
      setProfileExists(false);
      setIsLoading(false);
    } finally {
      isValidating.current = false;
    }
  }, [user, isLoading]);

  // Only validate when user changes
  useEffect(() => {
    checkSecurity();
  }, [user?.id]); // Only depend on user ID changes

  return (
    <SecurityContext.Provider value={{ 
      isSecure, 
      isLoading, 
      checkSecurity, 
      sessionValid, 
      profileExists 
    }}>
      {children}
    </SecurityContext.Provider>
  );
}

export function useSecurity() {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
}
